import React from 'react'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import SvgCheckFilled from 'src/components/Svgs/SvgCheckFilled'
import { useTranslate } from 'src/localization/Trns'
import {
  AttachmentVerification,
  ReceiptVerificationStatus,
  receiptVerificationStatusToTooltip,
} from 'src/models/receiptVerification.models'
import { css } from 'styled-components'

interface AttachmentVerificationBadgeProps {
  status?: ReceiptVerificationStatus
  verification?: AttachmentVerification
  size?: number
}

// Figma: Receipt-Image-Verification flyout badge colors for the bottom-right
// overlay on the 40px thumbnail.
const VERIFIED_DISC_COLOR = '#006E65'
const NOT_VERIFIED_DISC_COLOR = '#D97706'

// Visual-only badge rendered on the thumbnail (bottom-right). Hover tooltip
// is handled by whichever parent wraps the thumbnail (e.g. AttachmentThumbnail).
export default function AttachmentVerificationBadge({
  status,
  verification,
  size = 20,
}: AttachmentVerificationBadgeProps): React.ReactElement | null {
  const translate = useTranslate()
  const effectiveStatus = verification?.status ?? status
  if (!effectiveStatus) return null

  const label = translate(receiptVerificationStatusToTooltip(effectiveStatus))
  const icon = getBadgeIcon(effectiveStatus, size)
  if (!icon) return null

  return (
    <span
      role="img"
      aria-label={label}
      data-testid="attachment-verification-badge"
      css={css`
        display: inline-flex;
        width: ${size}px;
        height: ${size}px;
      `}
    >
      {icon}
    </span>
  )
}

function getBadgeIcon(
  status: ReceiptVerificationStatus,
  size: number,
): React.ReactElement | null {
  switch (status) {
    case ReceiptVerificationStatus.AutoMatched:
    case ReceiptVerificationStatus.Verified:
      return (
        <SvgCheckFilled height={size} width={size} fill={VERIFIED_DISC_COLOR} />
      )
    case ReceiptVerificationStatus.NotVerified:
    case ReceiptVerificationStatus.Mismatched:
    case ReceiptVerificationStatus.Unreadable:
      return (
        <SvgAlertCircledFilled
          height={size}
          width={size}
          fill={NOT_VERIFIED_DISC_COLOR}
        />
      )
    case ReceiptVerificationStatus.Verifying:
    case ReceiptVerificationStatus.Missing:
    default:
      return null
  }
}
